import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MoveHorizontal } from 'lucide-react';
import FadeInOnScroll from './FadeInOnScroll';

const samples = [
  {
    id: 'iphone-screen',
    label: 'iPhone Screen Replacement',
    before:
      'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?auto=format&fit=crop&w=1200&q=70',
    after:
      'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=1200&q=70',
  },
  {
    id: 'samsung-back',
    label: 'Samsung Back Glass Repair',
    before:
      'https://images.unsplash.com/photo-1556656793-08538906a9f8?auto=format&fit=crop&w=1200&q=70',
    after:
      'https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?auto=format&fit=crop&w=1200&q=70',
  },
  {
    id: 'laptop-keyboard',
    label: 'Laptop Restoration',
    before:
      'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=1200&q=70',
    after:
      'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=1200&q=70',
  },
];

const Slider = ({ before, after, label }) => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(55);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(cx);
  };
  const onMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(cx);
    },
    [updateFromClientX]
  );
  const onUp = () => {
    dragging.current = false;
  };

  useEffect(() => {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [onMove]);

  return (
    <div className="space-y-3" data-testid={`ba-card-${label}`}>
      <div
        ref={wrapRef}
        className="ba-slider aspect-[4/3] bg-gray-900"
        onMouseDown={onDown}
        onTouchStart={onDown}
      >
        <img src={before} alt={`${label} before`} draggable="false" />
        <div className="ba-after-wrap" style={{ width: `${pos}%` }}>
          <img
            src={after}
            alt={`${label} after`}
            draggable="false"
            style={{ width: `${100 / (pos / 100)}%`, maxWidth: 'none' }}
          />
        </div>
        <span className="ba-label" style={{ left: 16 }}>Before</span>
        <span className="ba-label" style={{ right: 16, background: 'rgba(0, 179, 179, 0.85)' }}>
          After
        </span>
        <div className="ba-handle" style={{ left: `${pos}%` }}>
          <div className="ba-handle-knob">
            <MoveHorizontal className="w-5 h-5" />
          </div>
        </div>
      </div>
      <p className="text-center text-gray-700 font-semibold">{label}</p>
    </div>
  );
};

const BeforeAfterSection = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto">
        <FadeInOnScroll className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Before <span style={{ color: '#00B3B3' }}>&</span> After
          </h2>
          <div className="w-24 h-1 mx-auto mb-5" style={{ backgroundColor: '#00B3B3' }} />
          <p className="text-gray-600 max-w-2xl mx-auto">
            Drag the slider to see real repair transformations done in our Round Lake Beach shop.
          </p>
        </FadeInOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {samples.map((s, i) => (
            <FadeInOnScroll key={s.id} delay={i * 120}>
              <Slider {...s} />
            </FadeInOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
